/**
 * Formatting Domain Logic
 *
 * Pure functions for display formatting.
 * No external dependencies.
 */

/**
 * Format a list of owner names with proper grammar.
 *
 * Examples:
 * - 0 owners: undefined
 * - 1 owner: "John Smith"
 * - 2 owners: "John Smith and Jane Doe"
 * - 3+ owners: "John Smith; Jane Doe; and Bob Wilson"
 *
 * @param {string[]} owners - Array of owner names
 * @returns {string|undefined} Formatted owner string
 */
export function formatOwnerNames(owners) {
  if (!owners || !Array.isArray(owners) || owners.length === 0) {
    return undefined;
  }

  if (owners.length === 1) {
    return owners[0];
  }

  if (owners.length === 2) {
    return `${owners[0]} and ${owners[1]}`;
  }

  // 3+ owners: "Owner1; Owner2; and Owner3"
  const allButLast = owners.slice(0, owners.length - 1).join('; ');
  const last = owners[owners.length - 1];
  return `${allButLast}; and ${last}`;
}

/**
 * Firebase error code to user-friendly message mapping.
 */
export const FirebaseErrorMessages = Object.freeze({
  'auth/wrong-password': 'Invalid email or password',
  'auth/user-not-found': 'Invalid email or password',
  'auth/invalid-email': 'Please enter a valid email address',
  'auth/user-disabled': 'This account has been disabled',
  'auth/email-already-in-use': 'An account with this email already exists',
  'auth/weak-password': 'Password should be at least 6 characters',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled',
  'auth/requires-recent-login': 'Please sign in again to complete this action'
});

/**
 * Format a Firebase error into a user-friendly message.
 *
 * @param {Object} error - Firebase error object
 * @param {string} [error.code] - Firebase error code
 * @param {string} [error.message] - Firebase error message
 * @returns {string} User-friendly error message
 */
export function formatFirebaseError(error) {
  if (!error) return 'An unknown error occurred';

  // Check for known error codes
  if (error.code && FirebaseErrorMessages[error.code]) {
    return FirebaseErrorMessages[error.code];
  }

  // Fall back to cleaned message
  if (error.message) {
    return error.message.replace('Firebase:', '').trim();
  }

  return 'An unknown error occurred';
}

/**
 * Format a date in long format: "January 15, 2025"
 *
 * @param {Date|string|number} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateLong(date) {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format a date in short format: "1/15/25"
 *
 * @param {Date|string|number} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateShort(date) {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year = d.getFullYear() % 100;

  return `${month}/${day}/${year.toString().padStart(2, '0')}`;
}

/**
 * Format a latitude/longitude pair as a string.
 *
 * @param {Object} latlng - Coordinates object
 * @param {number} latlng.lat - Latitude
 * @param {number} latlng.lng - Longitude
 * @param {number} [precision=6] - Decimal precision
 * @returns {string} Formatted coordinates (e.g., "43.6591, -70.2568")
 */
export function formatLatLng(latlng, precision = 6) {
  if (!latlng || latlng.lat == null || latlng.lng == null) {
    return '';
  }

  const lat = typeof latlng.lat === 'number' ? latlng.lat.toFixed(precision) : latlng.lat;
  const lng = typeof latlng.lng === 'number' ? latlng.lng.toFixed(precision) : latlng.lng;

  return `${lat}, ${lng}`;
}

/**
 * Capitalize the first letter of a string.
 *
 * @param {string} str - Input string
 * @returns {string} Capitalized string
 */
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format a number with thousand separators.
 *
 * @param {number} num - Number to format
 * @returns {string} Formatted number (e.g., "1,234,567")
 */
export function formatNumber(num) {
  if (num == null || isNaN(num)) return '';
  return num.toLocaleString('en-US');
}

/**
 * Format a currency amount.
 *
 * @param {number} amount - Amount to format
 * @param {string} [currency='USD'] - Currency code
 * @returns {string} Formatted currency (e.g., "$1,234.56")
 */
export function formatCurrency(amount, currency = 'USD') {
  if (amount == null || isNaN(amount)) return '';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency
  });
}
